import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type AllocationRow,
  DESIGNATION_OPTIONS,
  listPractitionerAllocation,
} from "@/features/practitioners/api";
import { TabShell } from "@/features/settings/components/tab-shell";

const DESIGNATION_LABELS = Object.fromEntries(
  DESIGNATION_OPTIONS.map((o) => [o.value, o.label]),
);

/**
 * Practitioner Allocation report (Settings → Practitioners → Allocation):
 * each practitioner and the live clients assigned to them. Read-only; Excel
 * export is deferred (display only for now).
 */
export function PractitionerAllocationPage() {
  const [rows, setRows] = useState<AllocationRow[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    listPractitionerAllocation()
      .then((r) => active && setRows(r))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  return (
    <TabShell
      title="Practitioner allocation"
      description="Which clients each practitioner is assigned to. Export is coming later."
      loading={!rows && !error}
      error={error}
      action={
        <Button asChild size="sm" variant="outline">
          <Link to="/practitioners">Back to practitioners</Link>
        </Button>
      }
    >
      {rows &&
        (rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">No practitioners yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Practitioner</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead>Clients</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium align-top">
                      <span className="flex items-center gap-2">
                        {row.fullName}
                        {row.defaultDesignation === "PRIMARY" && (
                          <Badge variant="secondary">Default</Badge>
                        )}
                        {row.defaultDesignation === "SECONDARY" && (
                          <Badge variant="outline">Secondary</Badge>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground align-top">
                      {DESIGNATION_LABELS[row.designation] ?? row.designation}
                    </TableCell>
                    <TableCell>
                      {row.clients.length === 0 ? (
                        <span className="text-muted-foreground">No clients assigned</span>
                      ) : (
                        <ul className="grid gap-1.5">
                          {row.clients.map((c) => (
                            <li key={c.id} className="flex items-center gap-2">
                              <Link
                                to={`/client/${c.id}/overview`}
                                className="hover:text-primary font-medium"
                              >
                                {c.displayName || "Unnamed client"}
                              </Link>
                              <Badge variant="outline" className="text-xs font-normal">
                                {c.irsStatus}
                              </Badge>
                            </li>
                          ))}
                        </ul>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
    </TabShell>
  );
}
